"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Button } from "../button.js";
import { usePreferences } from "../../lib/preferences-context.js";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  className?: string;
}

const faqData: FAQItem[] = [
  {
    question: "How do I connect my Google Calendar?",
    answer: "Navigate to the Google setup page and follow the OAuth authorization process. You'll be redirected to Google to grant permissions, then back to our application."
  },
  {
    question: "How do I connect my Microsoft Calendar?",
    answer: "Go to the Microsoft setup page and complete the OAuth flow. You'll need to authorize the application to access your Outlook calendar."
  },
  {
    question: "Can I view events from multiple calendars?",
    answer: "Yes! The unified view combines events from all your connected calendars, with color coding to distinguish between sources."
  },
  {
    question: "How do I export my calendar events?",
    answer: "Use the export button in the calendar view to download your events as a CSV file. This includes all events from your connected calendars."
  },
  {
    question: "What is Go Mode?",
    answer: "Go Mode is a streamlined view that shows events in a list format for quick scanning and management. Enable it in your profile preferences."
  },
  {
    question: "How do I edit or delete events?",
    answer: "Click on any event to open the details modal. From there, you can edit event details or delete the event entirely."
  }
];

export function FAQSection({ className }: FAQSectionProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const { preferences, updatePreferences } = usePreferences();

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const toggleFAQVisibility = () => {
    updatePreferences({ hideFAQ: !preferences.hideFAQ });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <HelpCircle className="h-5 w-5" />
            <span>Frequently Asked Questions</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFAQVisibility}
            className="h-8 w-8 p-0"
            title={preferences.hideFAQ ? "Show FAQ" : "Hide FAQ"}
          >
            {preferences.hideFAQ ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {faqData.map((item, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden"
            >
              <Button
                variant="ghost"
                className="w-full justify-between p-4 h-auto"
                onClick={() => toggleItem(index)}
              >
                <span className="text-left font-medium">{item.question}</span>
                {expandedItems.has(index) ? (
                  <ChevronUp className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                )}
              </Button>
              {expandedItems.has(index) && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
} 